import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type UserNumbersPreloaderService from '@/services/preloaders/userNumbersPreloaderService'
import type UserNumbersService from '@/services/domain/user/userNumbersService'
import type TranslateService from '@/services/translateService'
import type UserService from '@/services/domain/user/userService'
import type PhoneService from '@/services/phoneService'
import type SenderSettingsRouteHelperService from '@/services/domain/senderSettings/senderSettingsRouteHelperService'
import type { SenderChannelManager } from '@/services/domain/senderSettings/senderChannelManager'
import type { ChannelNumberType, ChannelSender } from '@/services/domain/senderSettings/senderChannelTypes'
import { isChannelNumberType } from '@/services/domain/senderSettings/senderChannelTypes'
import {
  type AddOptionsSection,
  type FieldParams,
  type FieldSelectSearchSenderDataResult,
  type GetCallsOptionsParams,
  isChannelMapSenderParams,
  type MapSenderSourcesToSubItemsParams,
} from '@/services/domain/senderSettings/types'
import type { ParsedUserNumbers, UserNumbersKeys } from '@/services/domain/user/types'
import type ByocHelperService from '@/services/domain/byoc/byocHelperService'
import type ByocService from '@/services/domain/byoc/byocService'
import type { ByocProvider } from '@/data/models/domain/services/byoc/types'
import {
  selectOptionLabelKey,
  selectOptionValueKey,
  type SenderOptionBadge,
  type SenderSettingsOption,
  type SenderSettingsOverrideOption,
  type SenderSettingsSender,
  type SenderSettingsSubheader,
} from '@/services/forms/types'
import type { Dict, ModelRaw } from '@/types'
import { PhoneType, type TmCountryCode } from '@/services/types'
import type SenderSettingsCountry from '@/data/models/domain/SenderSettingsCountry'
import Country from '@/data/models/domain/Country'
import { UserSettingsRoutes } from '@/routes/user/settings'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import type { SenderSourceType } from '@/services/domain/senderSource/types'
import { SenderSourceRegistrationStatus } from '@/services/domain/senderSource/types'
import { ByocCarrierStatus } from '@/services/domain/byoc/types'
import { ByocPhoneStatus } from '@/data/models/domain/services/byoc/ByocCarrierPhone'
import type UserSettingsService from '@/services/domain/user/userSettingsService'
import type PhoneNumbersService from '@/services/domain/phoneNumbers/phoneNumbersService'
import type { ChannelFilterValue } from '@/data/models/domain/chats/types'
import { addPlusToPhoneNumber } from '@/utils/string/addPlusToPhoneNumber'
import { escapeText } from '@/utils/string/escapeText'
import type SenderSource from '@/data/models/domain/SenderSource'

@injectable()
export default class FieldSelectSearchSenderOptionsBuilder {
  private optionsCache: Record<string, SenderSettingsOption[]> = {}

  constructor(
    @inject(SERVICE_TYPES.UserNumbersPreloaderService)
    protected readonly userNumbersPreloaderService: UserNumbersPreloaderService,
    @inject(SERVICE_TYPES.UserNumbersService)
    protected readonly userNumbersService: UserNumbersService,
    @inject(SERVICE_TYPES.TranslateService)
    protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.UserService)
    protected readonly userService: UserService,
    @inject(SERVICE_TYPES.SenderSettingsRouteHelperService)
    protected readonly senderSettingsRouteHelperService: SenderSettingsRouteHelperService,
    @inject(SERVICE_TYPES.PhoneService)
    protected readonly phoneService: PhoneService,
    @inject(SERVICE_TYPES.UserSettingsService)
    protected readonly userSettingsService: UserSettingsService,
    @inject(SERVICE_TYPES.ByocHelperService)
    protected readonly byocHelperService: ByocHelperService,
    @inject(SERVICE_TYPES.ByocService)
    protected readonly byocService: ByocService,
    @inject(SERVICE_TYPES.PhoneNumbersService)
    protected readonly phoneNumbersService: PhoneNumbersService,
    @inject(SERVICE_TYPES.SenderChannelManager)
    protected readonly senderChannelManager: SenderChannelManager,
  ) {}

  public async fetchData(
    enableByocSenderId?: boolean,
    hideOptionsFor?: UserNumbersKeys[],
  ): Promise<FieldSelectSearchSenderDataResult> {
    const [userNumbers, whatsApp, widgets, facebook, instagram] = await Promise.all([
      this.fetchUserNumbers(enableByocSenderId),
      this.fetchChannel('whatsApp', hideOptionsFor),
      this.fetchChannel('widgets', hideOptionsFor),
      this.fetchChannel('facebook', hideOptionsFor),
      this.fetchChannel('instagram', hideOptionsFor),
    ])

    await this.preloadByocSettings(userNumbers.byoc)

    return {
      ...userNumbers,
      whatsApp,
      widgets,
      facebook,
      instagram,
    }
  }

  private async preloadByocSettings(byoc: ParsedUserNumbers['byoc']) {
    if (!Object.values(byoc).some((senders) => senders.length)) {
      return
    }
    try {
      await this.byocService.getSettings()
    } catch {
      // A failed fetch must not break the whole sender list
    }
  }

  private async fetchUserNumbers(enableByocSenderId?: boolean) {
    await this.userNumbersPreloaderService.preload()
    const numbers = this.userNumbersService.findUserNumbersOrFail()
    return this.userNumbersService.parseUserNumbers(numbers, enableByocSenderId)
  }

  private async fetchChannel<K extends ChannelNumberType>(
    numberType: K,
    hideOptionsFor?: UserNumbersKeys[],
  ): Promise<ChannelSender<K>[]> {
    if (hideOptionsFor?.includes(numberType)) {
      return []
    }
    const provider = await this.senderChannelManager.resolveProvider(numberType)
    return provider.fetchSenders()
  }

  public getSenderSourceRegistrationBadge(status: SenderSourceRegistrationStatus) {
    const registrationStatusToSenderOptionBadgeMap: Record<SenderSourceRegistrationStatus, SenderOptionBadge> = {
      [SenderSourceRegistrationStatus.REGISTERED]: {
        color: 'green',
        label: this.translateService.t('fieldSelectSearchSenderSettings.registrationStatuses.registered'),
      },
      [SenderSourceRegistrationStatus.IN_REVIEW]: {
        color: 'blue',
        label: this.translateService.t('fieldSelectSearchSenderSettings.registrationStatuses.inReview'),
      },
      [SenderSourceRegistrationStatus.ACTION_REQUIRED]: {
        color: 'orange',
        label: this.translateService.t('fieldSelectSearchSenderSettings.registrationStatuses.actionRequired'),
      },
      [SenderSourceRegistrationStatus.NOT_REGISTERED]: {
        color: 'orange',
        label: this.translateService.t('fieldSelectSearchSenderSettings.registrationStatuses.notRegistered'),
      },
      [SenderSourceRegistrationStatus.FAILED]: {
        color: 'red',
        label: this.translateService.t('fieldSelectSearchSenderSettings.registrationStatuses.failed'),
      },
    }

    return registrationStatusToSenderOptionBadgeMap[status]
  }

  public getDefaultOption() {
    return {
      [selectOptionLabelKey]: this.translateService.t('fieldSelectSearchSenderSettings.defaultCallerSelectValue'),
      [selectOptionValueKey]: '',
    }
  }

  public getOverrideOptionMap() {
    return {
      isNoAvailable: {
        senderIds: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.noAvailableSenderIds'),
          value: 'senderIds',
          disabled: true,
        },
      },
      isNotSupportedSenderIdMap: {
        senderIds: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.senderIDisNotSupported'),
          value: 'senderIds',
          disabled: true,
        },
      },
      isNotSupportedUserNumbersMap: {
        userNumbers: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.ownMobileSettingIsNotSupported'),
          value: 'userNumbers',
          disabled: true,
        },
      },
      isNotSupportedSharedNumbersMap: {
        shared: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.sharedMobileSettingIsNotSupported'),
          value: 'shared',
          disabled: true,
        },
      },
      isNotSupportedCountryMap: {
        senderIds: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.senderIDisNotSupported'),
          value: 'senderIds',
          disabled: true,
        },
        userNumbers: {
          overrideOption: true,
          type: 'string',
          text: this.translateService.t('fieldSelectSearchSenderSettings.ownMobileSettingIsNotSupported'),
          value: 'userNumbers',
          disabled: true,
        },
      },
      senderSettingsEmptyMap: {
        dedicated: (countryId?: TmCountryCode) => ({
          overrideOption: true,
          type: 'button',
          to: this.senderSettingsRouteHelperService.getBuyNumberNumberRoute(countryId),
          text: this.translateService.t('fieldSelectSearchSenderSettings.buyTextmagicDedicatedNumber'),
          value: 'dedicated',
        }),
        senderIds: () => ({
          overrideOption: true,
          type: 'button',
          to: { name: UserSettingsRoutes.SmsVoiceSenderIds },
          text: this.translateService.t('fieldSelectSearchSenderSettings.applyForSenderID'),
          value: 'senderIds',
        }),
        userNumbers: () => ({
          overrideOption: true,
          type: 'button',
          to: { name: 'user.accounts.settings.profile' },
          text: !this.userService.hasUserPhone()
            ? this.translateService.t('fieldSelectSearchSenderSettings.addYourMobileNumberUnderAccount')
            : this.translateService.t('fieldSelectSearchSenderSettings.verifyYourMobileNumber'),
          value: 'userNumbers',
        }),
        byocNumbers: () => ({
          overrideOption: true,
          type: 'button',
          to: { name: UserSettingsRoutes.SmsVoiceByocFlow },
          text: this.translateService.t('fieldSelectSearchSenderSettings.connectCPaaSProvider'),
          value: 'byocNumbers',
        }),
      },
    }
  }

  public getOverrideOptionForCountry(
    numberType: UserNumbersKeys,
    senderSettingsCountry?: SenderSettingsCountry,
    showSenderIds = true,
  ): SenderSettingsOverrideOption | null {
    const overrideOptionMap = this.getOverrideOptionMap()
    if (!showSenderIds && overrideOptionMap.isNoAvailable[numberType]) {
      return overrideOptionMap.isNoAvailable[numberType] ?? null
    }
    if (
      senderSettingsCountry &&
      !senderSettingsCountry.ssiAllowSenderId &&
      overrideOptionMap.isNotSupportedSenderIdMap[numberType]
    ) {
      return overrideOptionMap.isNotSupportedSenderIdMap[numberType] ?? null
    }
    if (
      senderSettingsCountry &&
      !senderSettingsCountry.ssiAllowMobilePhone &&
      overrideOptionMap.isNotSupportedUserNumbersMap[numberType]
    ) {
      return overrideOptionMap.isNotSupportedUserNumbersMap[numberType] ?? null
    }
    if (
      senderSettingsCountry &&
      !senderSettingsCountry.ssiAllowShared &&
      overrideOptionMap.isNotSupportedSharedNumbersMap[numberType]
    ) {
      return overrideOptionMap.isNotSupportedSharedNumbersMap[numberType] ?? null
    }
    if (
      !senderSettingsCountry &&
      !this.userService.isUserCountrySupportsAllSenderSettings() &&
      overrideOptionMap.isNotSupportedCountryMap[numberType]
    ) {
      return overrideOptionMap.isNotSupportedCountryMap[numberType] ?? null
    }
    return null
  }

  public getOptionsMap(options: SenderSettingsOption[]) {
    return options.reduce<Record<string, SenderSettingsOption>>((acc, el: SenderSettingsOption) => {
      if (selectOptionValueKey in el) {
        acc[el[selectOptionValueKey]] = el
      }
      return acc
    }, {})
  }

  public getSubItems(
    title: string,
    senderItems: SenderSettingsSender[],
    numberType: UserNumbersKeys,
    senderSettingsCountry?: SenderSettingsCountry,
    hideOverrideOptionsFor?: UserNumbersKeys[],
    hideEmptyOptionsFor?: UserNumbersKeys[],
    hideOptionsFor?: UserNumbersKeys[],
    hideTitleOptionsFor?: UserNumbersKeys[],
    isOpenBuyTMDedicatedNumberInNewTab?: boolean,
  ): SenderSettingsOption[] {
    const data: SenderSettingsOption[] = []
    if (hideOptionsFor?.includes(numberType)) {
      return data
    }

    const subheader = {
      subheader: title,
      text: numberType,
      value: numberType,
      disabled: true,
    } as SenderSettingsSubheader
    const currentUserSettings = this.userSettingsService.currentUserSettingsOrFail()
    const overrideOption = this.getOverrideOptionForCountry(
      numberType,
      senderSettingsCountry,
      currentUserSettings.showSenderIds,
    )

    if (overrideOption && hideOverrideOptionsFor?.includes(numberType)) {
      return data
    }
    if (overrideOption) {
      data.push(subheader)
      data.push(overrideOption)
      return data
    }
    if (senderItems.length) {
      if (!hideTitleOptionsFor?.includes(numberType)) {
        data.push(subheader)
      }
      data.push(...senderItems)
      return data
    }
    if (hideEmptyOptionsFor?.includes(numberType)) {
      return data
    }
    const emptyOption = this.getEmptyOverrideOption(numberType, senderSettingsCountry?.countryId)
    if (emptyOption) {
      data.push(subheader)
      emptyOption.openInNewTab = isOpenBuyTMDedicatedNumberInNewTab
      data.push(emptyOption)
    }
    return data
  }

  private getEmptyOverrideOption(
    numberType: UserNumbersKeys,
    countryId?: TmCountryCode,
  ): SenderSettingsOverrideOption | undefined {
    const builder = this.getOverrideOptionMap().senderSettingsEmptyMap[numberType]
    if (builder) {
      return builder(countryId)
    }
    if (isChannelNumberType(numberType)) {
      return this.senderChannelManager.getProviderOrNull(numberType)?.getConnectOption?.()
    }
    return undefined
  }

  public async getOptions(params: FieldParams): Promise<SenderSettingsOption[]> {
    const {
      getDefaultOption,
      hideOverrideOptionsFor = [],
      hideEmptyOptionsFor = [],
      hideOptionsFor = ['whatsApp', 'widgets', 'facebook', 'instagram'],
      hideTitleOptionsFor = [],
      enableByocSenderId,
      isOpenBuyTMDedicatedNumberInNewTab,
      senderSettings,
    } = params

    const options: SenderSettingsOption[] = []
    const result = await this.fetchData(enableByocSenderId, hideOptionsFor)
    let senderSettingsCountry: SenderSettingsCountry | undefined
    if (senderSettings) {
      senderSettingsCountry = senderSettings.countries.find((country) => country.id === senderSettings.countryId)
    }

    const defaultOption = getDefaultOption ? getDefaultOption() : this.getDefaultOption()
    if (defaultOption) {
      options.push({
        ...defaultOption,
        isDefaultOption: true,
      })
    }

    const addOptionsSection = (optionsSection: AddOptionsSection) => {
      const subItems = optionsSection.senderSources
        //@todo need fix any type
        .map((sender) =>
          this.mapSenderSourcesToSubItems({
            numberType: optionsSection.numberType,
            sender,
            senderSettingsCountry,
          }),
        )
        .filter(Boolean) as SenderSettingsSender[]

      options.push(
        ...this.getSubItems(
          optionsSection.title,
          subItems,
          optionsSection.numberType,
          senderSettingsCountry,
          hideOverrideOptionsFor,
          hideEmptyOptionsFor,
          hideOptionsFor,
          hideTitleOptionsFor,
          isOpenBuyTMDedicatedNumberInNewTab,
        ),
      )
    }

    if (result.byoc) {
      const isEmpty = !Object.values(result.byoc).some((numbers) => numbers.length)
      if (isEmpty) {
        addOptionsSection({
          title: this.translateService.t('fieldSelectSearchSenderSettings.byocNumbers'),
          numberType: 'byocNumbers',
          senderSources: [],
        })
      } else {
        Object.entries(result.byoc).forEach(([key, senderSources]) => {
          if (!senderSources.length) return
          const provider = key as ByocProvider
          const providerLabel = this.byocHelperService.getProviderLabel(provider)
          const title = this.translateService.t('fieldSelectSearchSenderSettings.byoc', { provider: providerLabel })
          addOptionsSection({
            title,
            numberType: provider,
            senderSources,
          })
        })
      }
    } else {
      addOptionsSection({
        title: this.translateService.t('fieldSelectSearchSenderSettings.byocNumbers'),
        numberType: 'byocNumbers',
        senderSources: [],
      })
    }
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.dedicated'),
      numberType: 'dedicated',
      senderSources: result.dedicated,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.whatsApp'),
      numberType: 'whatsApp',
      senderSources: result.whatsApp,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.widgets'),
      numberType: 'widgets',
      senderSources: result.widgets,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.facebook'),
      numberType: 'facebook',
      senderSources: result.facebook,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.instagram'),
      numberType: 'instagram',
      senderSources: result.instagram,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.senderIds'),
      numberType: 'senderIds',
      senderSources: result.senderIds,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.userNumbers'),
      numberType: 'userNumbers',
      senderSources: result.userNumbers,
    })
    addOptionsSection({
      title: this.translateService.t('fieldSelectSearchSenderSettings.shared'),
      numberType: 'shared',
      senderSources: result.shared,
    })

    return options
  }

  public getCachedOptions(cacheKey: string): SenderSettingsOption[] | null {
    if (cacheKey) return this.optionsCache[cacheKey] ?? null
    return null
  }

  public setCachedOptions(cacheKey: string, options: SenderSettingsOption[]): void {
    this.optionsCache[cacheKey] = options
  }

  public clearCache(cacheKey?: string): void {
    if (cacheKey) {
      delete this.optionsCache[cacheKey]
    } else {
      this.optionsCache = {}
    }
  }

  private isValueChannelFilterValue(val: unknown): val is ChannelFilterValue {
    return !!val && typeof val === 'object' && 'type' in val
  }

  public filterOption<T>(option: SenderSettingsOption<T>, q: RegExp, rawQuery: string): boolean {
    const comparePhones = (val?: string | ChannelFilterValue) => {
      let phone: string | undefined | null
      if (this.isValueChannelFilterValue(val)) {
        phone = val.value
        phone ??= val.type
      } else {
        phone = val
      }
      if (!phone) return false
      const startsWithPlus = rawQuery.trim().startsWith('+')
      const phoneNumber = startsWithPlus ? addPlusToPhoneNumber(phone) : phone
      const cleanPhone = phoneNumber.replace(/[\s()./-]/g, '')
      const cleanQuery = rawQuery.replace(/[\s()./-]/g, '')
      return new RegExp(escapeText(cleanQuery)).test(cleanPhone)
    }

    const { subheader } = option as SenderSettingsSubheader
    const { text, additionalData, value, country } = option as SenderSettingsSender

    if (subheader) {
      return true
    }
    const textResult = text && q.test(text)
    if (textResult) {
      return textResult
    }
    const additionalDataResult = additionalData && q.test(additionalData)
    if (additionalDataResult) {
      return additionalDataResult
    }
    const phonesResult = comparePhones(value)
    if (phonesResult) {
      return phonesResult
    }
    if (country) {
      const countryResult = new RegExp(escapeText(rawQuery), 'i').test(country.name)
      if (countryResult) {
        return countryResult
      }
    }
    return false
  }

  public filterOptions<T>(options: SenderSettingsOption<T>[], search: string): SenderSettingsOption<T>[] {
    const query = new RegExp(escapeText(search), 'i')
    const filteredOption: SenderSettingsOption<T>[] = []
    options.forEach((t) => {
      if (!this.filterOption(t, query, search)) {
        return
      }
      filteredOption.push(t)
    })
    return filteredOption
  }

  public async getCallsOptions(params: GetCallsOptionsParams = {}): Promise<SenderSettingsOption[]> {
    const { hideEmptyOptionsFor = [], hideTitleOptionsFor = [] } = params
    const options: SenderSettingsOption[] = []

    const res = await this.phoneNumbersService.getVoiceableNumbers()

    const numberType = 'dedicated'
    const subItems = res.items
      .map((sender) =>
        this.mapSenderSourcesToSubItems({
          numberType,
          sender,
        }),
      )
      .filter(Boolean) as SenderSettingsSender[]

    options.push(
      ...this.getSubItems(
        this.translateService.t('fieldSelectSearchSenderSettings.voiceEnabled'),
        subItems,
        numberType,
        undefined,
        undefined,
        hideEmptyOptionsFor,
        undefined,
        hideTitleOptionsFor,
      ),
    )

    return options
  }

  private getSenderAdditionalDataLabel(sender: Pick<ModelRaw<SenderSource>, 'label'>, country?: Country): string {
    return sender.label || country?.name || ''
  }

  public mapSenderSourcesToSubItems(
    params: MapSenderSourcesToSubItemsParams,
  ): SenderSettingsSender<string | Dict<unknown>> | null {
    if (isChannelMapSenderParams(params)) {
      const provider = this.senderChannelManager.getProviderOrNull(params.numberType)
      return provider ? provider.mapToSubItem(params.sender) : null
    }

    const { numberType, sender, senderSettingsCountry } = params

    let isByoc = false
    let phoneType: PhoneType = PhoneType.DEDICATED
    let text: string
    let additionalData: string
    let value: string
    let isSenderId = false
    let registrationStatus: SenderSourceRegistrationStatus | undefined
    let isByocCarrierStatusFailed = false
    let country: Country | undefined = new Country()
    let sourceType: SenderSourceType | undefined

    if (numberType === 'dedicated') {
      country = this.phoneService.getCountryByPhone(sender.phone) ?? undefined
      if (
        country &&
        senderSettingsCountry?.isNativeDedicatedOnly &&
        !senderSettingsCountry?.allowedPhoneNumberCountryForNativeDedicatedOnly.includes(country.id)
      ) {
        return null
      }
      text = this.phoneService.preparePhoneForDisplay(sender.phone)
      additionalData = this.getSenderAdditionalDataLabel(sender, country)
      value = sender.phone
      if ('registration' in sender) {
        registrationStatus = sender.registration?.registrationStatus
      }
    } else if (numberType === 'senderIds') {
      country = undefined
      isSenderId = true
      text = sender.phone
      additionalData = sender.label ?? ''
      value = sender.phone
      sourceType = sender.type
    } else if (numberType === 'userNumbers' || numberType === 'shared') {
      country = this.phoneService.getCountryByPhone(sender.phone) ?? undefined
      isSenderId = false
      text = this.phoneService.preparePhoneForDisplay(sender.phone)
      additionalData = this.getSenderAdditionalDataLabel(sender, country)
      value = sender.phone
      sourceType = sender.type
    } else if (
      numberType === 'byocNumbers' ||
      numberType === 'twilio' ||
      numberType === 'vonage' ||
      numberType === 'bandwidth' ||
      numberType === 'sinch' ||
      numberType === 'infobip' ||
      numberType === 'messagebird' ||
      numberType === 'aws' ||
      numberType === 'telnyx'
    ) {
      isSenderId = this.userNumbersService.isSenderId(sender)
      country = this.phoneService.getCountryByPhone(sender.phone) ?? undefined
      isByoc = true
      phoneType = PhoneType.BYOC
      text = isSenderId ? sender.phone : this.phoneService.preparePhoneForDisplay(sender.phone)
      additionalData = this.getSenderAdditionalDataLabel(sender, country)
      value = sender.phone
      registrationStatus = sender.registration?.registrationStatus
      isByocCarrierStatusFailed = sender.carrierStatus === ByocCarrierStatus.FAILED
      sourceType = sender.type
    } else {
      throw new TmAbsurdError(numberType)
    }

    if (text === additionalData) {
      additionalData = ''
    }

    const item: SenderSettingsSender<string | Dict<unknown>> = {
      isSenderSettings: true,
      isByoc,
      sourceType,
      isSenderId,
      value,
      text,
      additionalData,
      id: sender.id,
      country,
      phoneType,
      badge: registrationStatus ? this.getSenderSourceRegistrationBadge(registrationStatus) : undefined,
      numberType,
    }

    const isByocPhoneStatusFailed = sender.status === ByocPhoneStatus.FAILED

    if (isByocCarrierStatusFailed || isByocPhoneStatusFailed) {
      item.disabled = true
      item.error = true
      item.icon = 'error'
      item.tooltip = {
        isSimple: true,
        keypath: isByocCarrierStatusFailed ? 'byocProviderCell.failedAuth' : 'byocPhoneCell.tooltip',
      }
    } else if (isByoc && !this.byocService.isSendingAllowed()) {
      item.disabled = true
      item.icon = 'error'
      item.tooltip = {
        isSimple: true,
        keypath: 'fieldSelectSearchSenderSettings.byocSendingNotAllowed',
      }
    }

    return item
  }
}
